// Bake harness (Jun 28) — DEV-ONLY headless render target for Playwright.
//
// The production text-overlay bake renders the REAL JsonSocialPost (template +
// AI copy over the text-free scene) and screenshots it, so the baked tile is
// pixel-identical to what the editor shows. Playwright drives this page:
//   1. add_init_script sets window.__BAKE_PARAMS__ = { template, slots, sceneB64 }
//      BEFORE load (scene b64 is ~1MB — too big for a URL param).
//   2. this page renders <JsonSocialPost> + mergedSceneUrl at native canvas size
//      inside #bake-root, then sets window.__BAKE_READY__ once fonts + paint settle.
//   3. Playwright waits for __BAKE_READY__ and screenshots #bake-root → PNG.
//
// Not linked anywhere, no AuthGate (Playwright loads it headless). DEV proof;
// production ships a self-contained build of this (Phase 2).

import { useEffect, useState } from 'react';
import JsonSocialPost from '@/pages/app/components/social-designs/json-templates/JsonSocialPost';
import type { JsonTemplate } from '@/pages/app/components/social-designs/json-templates/types';
import type { SocialPostSlots } from '@/lib/api';

type BakeParams = { template: JsonTemplate; slots: SocialPostSlots; sceneB64: string };

declare global {
  interface Window {
    __BAKE_PARAMS__?: BakeParams;
    __BAKE_READY__?: boolean;
  }
}

export default function BakeHarnessPage() {
  const [params, setParams] = useState<BakeParams | null>(null);

  // Params are injected via add_init_script (before load) but poll briefly in
  // case they land just after mount.
  useEffect(() => {
    let tries = 0;
    const tick = () => {
      if (window.__BAKE_PARAMS__) {
        setParams(window.__BAKE_PARAMS__);
        return;
      }
      if (tries++ < 150) setTimeout(tick, 20);
    };
    tick();
  }, []);

  // Signal ready only after web fonts load (text metrics + wrap depend on them)
  // AND two animation frames so layout/paint has settled.
  useEffect(() => {
    if (!params) return;
    let cancelled = false;
    const markReady = async () => {
      if (document.fonts && document.fonts.ready) {
        try { await document.fonts.ready; } catch { /* ignore */ }
      }
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (!cancelled) window.__BAKE_READY__ = true;
        }),
      );
    };
    void markReady();
    return () => { cancelled = true; };
  }, [params]);

  if (!params) return <div id="bake-pending" />;

  const w = params.template.canvas?.width || 1080;
  const h = params.template.canvas?.height || 1080;
  return (
    <div id="bake-root" style={{ width: w, height: h, position: 'relative', overflow: 'hidden' }}>
      <JsonSocialPost
        config={params.template}
        slots={params.slots}
        productImageUrl=""
        mergedSceneUrl={`data:image/png;base64,${params.sceneB64}`}
      />
    </div>
  );
}
